import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Delete, Eye, Pencil, Trash, Settings } from "lucide-react";
import { Switch, Menu, MenuItem, IconButton, Checkbox } from "@mui/material";
import { NavLink } from "react-router-dom";
import { Permissions } from "../../../../../permissions";
import {
  errorToast,
  infoToast,
  successToast,
} from "../../../../../services/toast.service";
import {
  useFetchAllVendorProductsQuery,
  useUpdateProductStatusMutation,
} from "../../../../../services/baseQuery";
import { CircularProgress } from "@mui/material";
import { useCallback, useEffect } from "react";
// Import dummy data
import {
  dummyProducts,
  dummyProductsResponse,
  dummyCategoryTree,
  transformCategories,
} from "../../../../../dummyData";

const defaultColumns = [
  { id: "productId", label: "Product ID", minWidth: "140px", required: true },
  {
    id: "productName",
    label: "Product Name",
    minWidth: "200px",
    required: true,
  },
  { id: "brand", label: "Brand", minWidth: "180px", required: false },
  { id: "category", label: "Category", minWidth: "200px", required: false },
  { id: "size", label: "Size", minWidth: "120px", required: false },
  { id: "mrp", label: "MRP (₹)", minWidth: "120px", required: true },
  { id: "sp", label: "SP (₹)", minWidth: "120px", required: true },
  { id: "quantity", label: "Quantity", minWidth: "100px", required: false },
  { id: "soldAs", label: "Sold As", minWidth: "120px", required: false },
  { id: "status", label: "Status", minWidth: "100px", required: true },
  { id: "action", label: "Action", minWidth: "120px", required: true },
];

export default function ProductTable({
  onSettingsClick,
  anchorEl,
  onCloseSettings,
  visibleColumns,
  onColumnToggle,
  setRefetchFn,
  searchQuery,
  filters,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isLoading, setIsLoading] = React.useState(false);
  const [filteredData, setFilteredData] = React.useState({
    products: [],
    totalCount: 0,
  });

  // Get flattened categories for display
  const flattenedCategories = React.useMemo(() => {
    return transformCategories(dummyCategoryTree);
  }, []);

  // Use dummy data instead of API
  React.useEffect(() => {
    // Simulate loading
    setIsLoading(true);

    setTimeout(() => {
      let filtered = [...dummyProducts];

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          product =>
            product.productId.toLowerCase().includes(query) ||
            product.name.toLowerCase().includes(query) ||
            product.brand.name.toLowerCase().includes(query),
        );
      }

      // Apply other filters
      if (filters.brand) {
        filtered = filtered.filter(
          product => product.brand.name === filters.brand,
        );
      }

      if (filters.category) {
        filtered = filtered.filter(
          product =>
            product.categories.includes(filters.category) ||
            product.primary_category === filters.category,
        );
      }

      if (filters.status) {
        const isInStock = filters.status === "in_stock";
        filtered = filtered.filter(product => product.inStock === isInStock);
      }

      // Apply sorting
      if (filters.sortBy) {
        const [field, direction] = filters.sortBy.split("_");

        filtered.sort((a, b) => {
          let valueA, valueB;

          if (field === "name") {
            valueA = a.name;
            valueB = b.name;
          } else if (field === "price") {
            valueA = a.sellingPrice;
            valueB = b.sellingPrice;
          } else if (field === "date") {
            valueA = new Date(a.dateAdded);
            valueB = new Date(b.dateAdded);
          }

          if (direction === "asc") {
            return valueA > valueB ? 1 : -1;
          } else {
            return valueA < valueB ? 1 : -1;
          }
        });
      }

      setFilteredData({
        products: filtered,
        totalCount: filtered.length,
      });
      setIsLoading(false);
    }, 800); // Simulate network delay
  }, [searchQuery, filters, page, rowsPerPage]);

  // Reset to first page when filters or search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Filter visible columns
  const visibleColumnsList = defaultColumns.filter(
    column => visibleColumns[column.id],
  );

  let editProduct =
    Permissions.find(permission => permission.includes("edit_product")) !==
    undefined;

  let deleteProduct =
    Permissions.find(permission => permission.includes("delete_product")) !==
    undefined;

  // Helper function to get category display name
  const getCategoryDisplayName = categoryId => {
    const category = flattenedCategories.find(c => c.id === categoryId);
    return category ? category.name : "-";
  };

  // Handle status change
  const handleStatusChange = async (productId, currentStatus) => {
    try {
      // Simulate API call
      successToast(
        `Product ${
          currentStatus === "In Stock"
            ? "marked as out of stock"
            : "marked as in stock"
        } successfully`,
      );
    } catch (error) {
      errorToast("Failed to update product status");
    }
  };

  // Transform data to table format
  const rows = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    const displayedProducts = filteredData.products.slice(
      startIndex,
      startIndex + rowsPerPage,
    );

    return displayedProducts.map(product => ({
      _id: product._id,
      productId: product.productId,
      productName: product.name,
      brand: product.brand.name,
      category: getCategoryDisplayName(product.primary_category),
      size: product.size || "-",
      mrp: `₹${product.mrp.toLocaleString()}`,
      sp: `₹${product.sellingPrice.toLocaleString()}`,
      quantity: product.quantity,
      soldAs: product.soldAs || "Unit",
      status: product.inStock ? "In Stock" : "Out of Stock",
    }));
  }, [filteredData, page, rowsPerPage, flattenedCategories]);

  // Create a dummy refetch function
  React.useEffect(() => {
    if (typeof setRefetchFn === "function") {
      setRefetchFn(() => () => {
        // setIsLoading(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 800);
      });
    }
  }, [setRefetchFn]);

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseSettings}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 200,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-600">Show Columns</p>
        </div>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => onColumnToggle(column.id)}
            disabled={column.required}
            dense>
            <Checkbox
              checked={visibleColumns[column.id]}
              disabled={column.required}
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": {
                  color: "#5652B0",
                },
              }}
            />
            <span
              className={`text-sm ${column.required ? "text-gray-400" : ""}`}>
              {column.label}
              {column.required && " *"}
            </span>
          </MenuItem>
        ))}
      </Menu>

      <TableContainer
        sx={{ maxHeight: "calc(100vh - 300px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumnsList.map(column => (
                <TableCell
                  key={column.id}
                  align="center"
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={40} />
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="py-4 text-gray-500">No products found</div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row._id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}>
                  {visibleColumnsList.map(column => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {column.id === "status" ? (
                          <div
                            className={`px-3 py-1 rounded-full text-sm font-medium inline-block
                            ${
                              value === "In Stock"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}>
                            {value}
                          </div>
                        ) : column.id === "action" ? (
                          <div className="flex items-center justify-center gap-3">
                            <NavLink
                              to={`/products/view/${row._id}`}
                              className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                              <Eye size={16} className="text-[#5652B0]" />
                            </NavLink>

                            {editProduct ? (
                              <div
                                onClick={() =>
                                  infoToast("Edit not available in demo.")
                                }>
                                {/* april demo comment: change below div to navlink and remove above div, post demo */}
                                <div
                                  // to={`/products/edit/${row._id}`}
                                  className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                                  <Pencil
                                    size={16}
                                    className="text-[#5652B0]"
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="p-1.5 rounded-md bg-gray-100 cursor-not-allowed">
                                <Pencil size={16} className="text-gray-400" />
                              </div>
                            )}
                          </div>
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredData.totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
        }}
      />

      {isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </Paper>
  );
}

export { defaultColumns };
